import React from 'react';
import {
  GlobeAltIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ComputerDesktopIcon,
  DocumentCheckIcon,
  ArrowPathIcon,
  EyeIcon,
  WrenchScrewdriverIcon,
  LockClosedIcon,
  CpuChipIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { SolutionPageTemplate } from '../../components/page-templates/MarketPageTemplates';

const painPoints = [
  {
    icon: ArrowPathIcon,
    title: 'High-Volume Device Cycles',
    description: 'You manage constant installations, collections and swaps across multiple regions',
  },
  {
    icon: TruckIcon,
    title: 'Last-Mile Complexity',
    description: 'Reliable delivery and returns, often to employee homes or distributed sites',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'Lifecycle Risk',
    description: 'Data wiping, repair and reuse must be fast, secure and fully auditable',
  },
  {
    icon: EyeIcon,
    title: 'Visibility Expectations',
    description: 'Enterprise clients expect real-time status and accurate asset records',
  },
];

const solutions = [
  {
    icon: DocumentCheckIcon,
    title: 'Regulatory & Import Compliance',
    points: [
      'IOR/EOR in complex jurisdictions',
      'Duties and taxes management',
      'Faster clearance across borders',
    ],
  },
  {
    icon: TruckIcon,
    title: 'Last-Mile Delivery & Returns',
    points: [
      'Reliable delivery to offices and employee homes',
      'Scheduled collection services',
      'Real-time tracking and visibility',
    ],
  },
  {
    icon: ArrowPathIcon,
    title: 'Reverse Logistics & Asset Recovery',
    points: [
      'Regional RMA intake and processing',
      'Rapid replacement workflows',
      'Audit-ready tracking and reporting',
    ],
  },
  {
    icon: LockClosedIcon,
    title: 'Secure Data Wiping',
    points: [
      'Certified destruction (NIST 800-88, ADISA)',
      'Compliant ITAD processes',
      'Fully traceable and auditable',
    ],
  },
  {
    icon: ComputerDesktopIcon,
    title: 'Device Imaging & Preparation',
    points: [
      'Software pre-load and configuration',
      'Labelling and asset tagging',
      'Installation-ready delivery',
    ],
  },
  {
    icon: WrenchScrewdriverIcon,
    title: 'Repair & Refurbishment',
    points: [
      'Certified repair workflows',
      'Refurbishment ready for redeployment',
      'Quality checks and validation',
    ],
  },
];

const whyChooseCards = [
  {
    icon: GlobeAltIcon,
    title: 'Scale Device Lifecycles Globally',
    description: 'Multi-country support without building internal logistics infrastructure in every market.',
    accent: 'bg-blue-500',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Reduce Lifecycle Risk',
    description: 'Secure handling and auditable processes protect your enterprise clients and their data.',
    accent: 'bg-cyan-500',
  },
  {
    icon: LinkIcon,
    title: 'Increase Stickiness',
    description: 'Better execution improves data accuracy and drives long-term contracts with enterprise customers.',
    accent: 'bg-indigo-500',
  },
];

const SaasDigitalPlatforms: React.FC = () => (
  <SolutionPageTemplate
    eyebrow="SaaS & Digital Platforms"
    title="Scale Your ITAM Operations Internationally"
    highlight="Internationally"
    description="TrueLog strengthens your commercial positioning by backing your ITAM operations with structured, compliant, real-world asset movement across 140+ countries."
    heroIcon={CpuChipIcon}
    heroAccentIcon={GlobeAltIcon}
    problemHeading="Physical execution at enterprise scale is complex"
    problemDescription="Your platform manages digital records, but the physical logistics behind device lifecycles create real operational risk."
    painPoints={painPoints}
    solutionsIntro="Complete physical execution for every stage of the device lifecycle."
    solutions={solutions}
    whyChooseCards={whyChooseCards}
    ctaTitle="Ready to strengthen your ITAM operations globally?"
    ctaDescription="Let TrueLog handle the physical execution so your platform can deliver a seamless device lifecycle experience."
  />
);

export default SaasDigitalPlatforms;
